import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/pt-br";
import "dayjs/locale/ko";
import "dayjs/locale/ja";
import "dayjs/locale/fr";
import "dayjs/locale/de";
import "dayjs/locale/tr";
import "dayjs/locale/vi";
import "dayjs/locale/zh-cn";
import api from "@/lib/api";

export const LANGUAGES = [
  { code: "en", label: "English", dayjs: "en" },
  { code: "es", label: "Español", dayjs: "es" },
  { code: "pt", label: "Português", dayjs: "pt-br" },
  { code: "fr", label: "Français", dayjs: "fr" },
  { code: "de", label: "Deutsch", dayjs: "de" },
  { code: "tr", label: "Türkçe", dayjs: "tr" },
  { code: "vi", label: "Tiếng Việt", dayjs: "vi" },
  { code: "ko", label: "한국어", dayjs: "ko" },
  { code: "ja", label: "日本語", dayjs: "ja" },
  { code: "zh", label: "中文", dayjs: "zh-cn" },
];
const SUPPORTED = LANGUAGES.map((l) => l.code);
export const SUPPORTED_LANGS = SUPPORTED;

const STRINGS: Record<string, Record<string, string>> = {
  en: { signin: "Sign in", subscribe: "Subscribe", save: "Save", saved: "Saved", share: "Share",
    published: "Published", breaking: "Breaking News", premium: "Premium", minRead: "min read",
    keyTakeaways: "Key Takeaways", quickAnswers: "Quick Answers", relatedStories: "Related Stories",
    trendingNow: "Trending Now", mostReadToday: "Most Read Today", translating: "Translating",
    myAccount: "My Account", myFeed: "My Feed", signOut: "Sign out", language: "Language",
    goPremium: "Go Premium", premiumStory: "This is a Premium story", by: "By",
    latestFeed: "Latest Feed", allLatestNews: "All latest news", jumpBackIn: "Jump back in",
    premiumDeepDives: "Premium Deep Dives", reads: "reads",
    briefArchive: "Daily Market Brief — view the full archive",
    "cat.latest": "Latest", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "Blockchain",
    "cat.crypto": "Crypto", "cat.government-policy": "Policy", "cat.markets": "Markets",
    "cat.explainers": "Explainers", "cat.research": "Research",
    followTitle: "Never miss a breaking story", followSub: "Big news drops fast — get it first on X and Telegram.",
    followX: "Follow on X", joinTelegram: "Join Telegram",
    upNext: "Up next", keepReading: "Keep reading" },
  es: { signin: "Iniciar sesión", subscribe: "Suscribirse", save: "Guardar", saved: "Guardado", share: "Compartir",
    published: "Publicado", breaking: "Última Hora", premium: "Premium", minRead: "min de lectura",
    keyTakeaways: "Puntos Clave", quickAnswers: "Respuestas Rápidas", relatedStories: "Noticias Relacionadas",
    trendingNow: "Tendencias", mostReadToday: "Lo Más Leído Hoy", translating: "Traduciendo",
    myAccount: "Mi Cuenta", myFeed: "Mi Feed", signOut: "Cerrar sesión", language: "Idioma",
    goPremium: "Hazte Premium", premiumStory: "Esta es una historia Premium", by: "Por",
    latestFeed: "Últimas Noticias", allLatestNews: "Todas las noticias", jumpBackIn: "Continuar leyendo",
    premiumDeepDives: "Análisis Premium", reads: "lecturas",
    briefArchive: "Resumen Diario del Mercado — ver el archivo completo",
    "cat.latest": "Últimas", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "Blockchain",
    "cat.crypto": "Cripto", "cat.government-policy": "Política", "cat.markets": "Mercados",
    "cat.explainers": "Explicativos", "cat.research": "Investigación",
    followTitle: "No te pierdas ninguna noticia de última hora", followSub: "Las grandes noticias vuelan — recíbelas primero en X y Telegram.",
    followX: "Seguir en X", joinTelegram: "Unirse a Telegram",
    upNext: "A continuación", keepReading: "Seguir leyendo" },
  pt: { signin: "Entrar", subscribe: "Assinar", save: "Salvar", saved: "Salvo", share: "Compartilhar",
    published: "Publicado", breaking: "Última Hora", premium: "Premium", minRead: "min de leitura",
    keyTakeaways: "Pontos-Chave", quickAnswers: "Respostas Rápidas", relatedStories: "Notícias Relacionadas",
    trendingNow: "Em Alta", mostReadToday: "Mais Lidas Hoje", translating: "Traduzindo",
    myAccount: "Minha Conta", myFeed: "Meu Feed", signOut: "Sair", language: "Idioma",
    goPremium: "Seja Premium", premiumStory: "Esta é uma matéria Premium", by: "Por",
    latestFeed: "Últimas Notícias", allLatestNews: "Todas as notícias", jumpBackIn: "Continuar lendo",
    premiumDeepDives: "Análises Premium", reads: "leituras",
    briefArchive: "Resumo Diário do Mercado — ver o arquivo completo",
    "cat.latest": "Últimas", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "Blockchain",
    "cat.crypto": "Cripto", "cat.government-policy": "Política", "cat.markets": "Mercados",
    "cat.explainers": "Explicativos", "cat.research": "Pesquisa",
    followTitle: "Não perca nenhuma notícia de última hora", followSub: "As grandes notícias chegam rápido — receba primeiro no X e no Telegram.",
    followX: "Seguir no X", joinTelegram: "Entrar no Telegram",
    upNext: "A seguir", keepReading: "Continue lendo" },
  fr: { signin: "Connexion", subscribe: "S'abonner", save: "Enregistrer", saved: "Enregistré", share: "Partager",
    published: "Publié", breaking: "Dernière Minute", premium: "Premium", minRead: "min de lecture",
    keyTakeaways: "Points Clés", quickAnswers: "Réponses Rapides", relatedStories: "Articles Liés",
    trendingNow: "Tendances", mostReadToday: "Les Plus Lus Aujourd'hui", translating: "Traduction",
    myAccount: "Mon Compte", myFeed: "Mon Flux", signOut: "Déconnexion", language: "Langue",
    goPremium: "Passer Premium", premiumStory: "Ceci est un article Premium", by: "Par",
    latestFeed: "Dernières Actus", allLatestNews: "Toutes les actualités", jumpBackIn: "Reprendre la lecture",
    premiumDeepDives: "Analyses Premium", reads: "lectures",
    briefArchive: "Brief Quotidien du Marché — voir toutes les archives",
    "cat.latest": "Dernières", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "Blockchain",
    "cat.crypto": "Crypto", "cat.government-policy": "Politique", "cat.markets": "Marchés",
    "cat.explainers": "Décryptages", "cat.research": "Recherche",
    followTitle: "Ne manquez aucune actualité de dernière minute", followSub: "Les grandes nouvelles vont vite — recevez-les en premier sur X et Telegram.",
    followX: "Suivre sur X", joinTelegram: "Rejoindre Telegram",
    upNext: "À suivre", keepReading: "Continuer la lecture" },
  de: { signin: "Anmelden", subscribe: "Abonnieren", save: "Speichern", saved: "Gespeichert", share: "Teilen",
    published: "Veröffentlicht", breaking: "Eilmeldung", premium: "Premium", minRead: "Min. Lesezeit",
    keyTakeaways: "Kernpunkte", quickAnswers: "Schnelle Antworten", relatedStories: "Ähnliche Artikel",
    trendingNow: "Im Trend", mostReadToday: "Heute Meistgelesen", translating: "Übersetzung",
    myAccount: "Mein Konto", myFeed: "Mein Feed", signOut: "Abmelden", language: "Sprache",
    goPremium: "Premium Holen", premiumStory: "Dies ist ein Premium-Artikel", by: "Von",
    followTitle: "Verpassen Sie keine Eilmeldung", followSub: "Große News verbreiten sich schnell — erhalten Sie sie zuerst auf X und Telegram.",
    followX: "Auf X folgen", joinTelegram: "Telegram beitreten",
    upNext: "Als Nächstes", keepReading: "Weiterlesen" },
  tr: { signin: "Giriş Yap", subscribe: "Abone Ol", save: "Kaydet", saved: "Kaydedildi", share: "Paylaş",
    published: "Yayınlandı", breaking: "Son Dakika", premium: "Premium", minRead: "dk okuma",
    keyTakeaways: "Öne Çıkanlar", quickAnswers: "Hızlı Yanıtlar", relatedStories: "İlgili Haberler",
    trendingNow: "Gündemdekiler", mostReadToday: "Bugün En Çok Okunanlar", translating: "Çevriliyor",
    myAccount: "Hesabım", myFeed: "Akışım", signOut: "Çıkış Yap", language: "Dil",
    goPremium: "Premium'a Geç", premiumStory: "Bu bir Premium haber", by: "Yazan",
    latestFeed: "Son Haberler", allLatestNews: "Tüm haberler", jumpBackIn: "Kaldığın yerden devam et",
    premiumDeepDives: "Premium Analizler", reads: "okunma",
    briefArchive: "Günlük Piyasa Özeti — tüm arşivi görüntüle",
    "cat.latest": "Son Dakika", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "Blockchain",
    "cat.crypto": "Kripto", "cat.government-policy": "Politika", "cat.markets": "Piyasalar",
    "cat.explainers": "Rehberler", "cat.research": "Araştırma",
    followTitle: "Hiçbir son dakika haberini kaçırmayın", followSub: "Büyük haberler hızlı gelir — X ve Telegram'da ilk siz alın.",
    followX: "X'te Takip Et", joinTelegram: "Telegram'a Katıl",
    upNext: "Sıradaki", keepReading: "Okumaya devam et" },
  vi: { signin: "Đăng nhập", subscribe: "Đăng ký", save: "Lưu", saved: "Đã lưu", share: "Chia sẻ",
    published: "Đăng", breaking: "Tin Nóng", premium: "Premium", minRead: "phút đọc",
    keyTakeaways: "Điểm Chính", quickAnswers: "Giải Đáp Nhanh", relatedStories: "Tin Liên Quan",
    trendingNow: "Xu Hướng", mostReadToday: "Đọc Nhiều Nhất Hôm Nay", translating: "Đang dịch",
    myAccount: "Tài Khoản", myFeed: "Bảng Tin", signOut: "Đăng xuất", language: "Ngôn ngữ",
    goPremium: "Nâng Cấp Premium", premiumStory: "Đây là bài viết Premium", by: "Bởi",
    latestFeed: "Tin Mới Nhất", allLatestNews: "Tất cả tin tức", jumpBackIn: "Đọc tiếp",
    premiumDeepDives: "Phân Tích Premium", reads: "lượt đọc",
    briefArchive: "Bản Tin Thị Trường Hằng Ngày — xem toàn bộ kho lưu trữ",
    "cat.latest": "Mới Nhất", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "Blockchain",
    "cat.crypto": "Crypto", "cat.government-policy": "Chính Sách", "cat.markets": "Thị Trường",
    "cat.explainers": "Giải Thích", "cat.research": "Nghiên Cứu",
    followTitle: "Đừng bỏ lỡ tin nóng nào", followSub: "Tin lớn đến rất nhanh — nhận sớm nhất trên X và Telegram.",
    followX: "Theo dõi trên X", joinTelegram: "Tham gia Telegram",
    upNext: "Tiếp theo", keepReading: "Đọc tiếp" },
  ko: { signin: "로그인", subscribe: "구독하기", save: "저장", saved: "저장됨", share: "공유",
    published: "게시", breaking: "속보", premium: "프리미엄", minRead: "분 소요",
    keyTakeaways: "핵심 요약", quickAnswers: "빠른 답변", relatedStories: "관련 기사",
    trendingNow: "실시간 인기", mostReadToday: "오늘 가장 많이 본 기사", translating: "번역 중",
    myAccount: "내 계정", myFeed: "내 피드", signOut: "로그아웃", language: "언어",
    goPremium: "프리미엄 가입", premiumStory: "프리미엄 기사입니다", by: "작성자",
    latestFeed: "최신 피드", allLatestNews: "모든 최신 뉴스", jumpBackIn: "이어서 읽기",
    premiumDeepDives: "프리미엄 심층 분석", reads: "회 읽음",
    briefArchive: "데일리 마켓 브리핑 — 전체 아카이브 보기",
    "cat.latest": "최신", "cat.web3": "웹3", "cat.defi": "디파이", "cat.blockchain": "블록체인",
    "cat.crypto": "크립토", "cat.government-policy": "정책", "cat.markets": "마켓",
    "cat.explainers": "해설", "cat.research": "리서치",
    followTitle: "속보를 놓치지 마세요", followSub: "빅 뉴스는 빠르게 움직입니다 — X와 텔레그램에서 가장 먼저 받아보세요.",
    followX: "X에서 팔로우", joinTelegram: "텔레그램 참여",
    upNext: "다음 기사", keepReading: "계속 읽기" },
  ja: { signin: "ログイン", subscribe: "購読する", save: "保存", saved: "保存済み", share: "共有",
    published: "公開", breaking: "速報", premium: "プレミアム", minRead: "分で読める",
    keyTakeaways: "要点", quickAnswers: "クイック回答", relatedStories: "関連記事",
    trendingNow: "トレンド", mostReadToday: "本日の人気記事", translating: "翻訳中",
    myAccount: "アカウント", myFeed: "マイフィード", signOut: "ログアウト", language: "言語",
    goPremium: "プレミアムへ", premiumStory: "プレミアム記事です", by: "著者",
    latestFeed: "最新フィード", allLatestNews: "すべての最新ニュース", jumpBackIn: "続きを読む",
    premiumDeepDives: "プレミアム特集", reads: "回閲覧",
    briefArchive: "デイリー・マーケット・ブリーフ — アーカイブを見る",
    "cat.latest": "最新", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "ブロックチェーン",
    "cat.crypto": "クリプト", "cat.government-policy": "政策", "cat.markets": "マーケット",
    "cat.explainers": "解説", "cat.research": "リサーチ",
    followTitle: "速報を見逃さない", followSub: "ビッグニュースは一瞬 — XとTelegramでいち早く受け取ろう。",
    followX: "Xでフォロー", joinTelegram: "Telegramに参加",
    upNext: "次の記事", keepReading: "続きを読む" },
  zh: { signin: "登录", subscribe: "订阅", save: "收藏", saved: "已收藏", share: "分享",
    published: "发布于", breaking: "突发新闻", premium: "高级版", minRead: "分钟阅读",
    keyTakeaways: "要点速览", quickAnswers: "快速问答", relatedStories: "相关报道",
    trendingNow: "热门趋势", mostReadToday: "今日最热", translating: "翻译中",
    myAccount: "我的账户", myFeed: "我的订阅", signOut: "退出登录", language: "语言",
    goPremium: "升级高级版", premiumStory: "这是一篇高级版文章", by: "作者",
    latestFeed: "最新动态", allLatestNews: "全部最新新闻", jumpBackIn: "继续阅读",
    premiumDeepDives: "高级深度报道", reads: "次阅读",
    briefArchive: "每日市场简报 — 查看完整存档",
    "cat.latest": "最新", "cat.web3": "Web3", "cat.defi": "DeFi", "cat.blockchain": "区块链",
    "cat.crypto": "加密货币", "cat.government-policy": "政策", "cat.markets": "市场",
    "cat.explainers": "科普解读", "cat.research": "研究",
    upNext: "下一篇", keepReading: "继续阅读" },
};

interface CardTx { title?: string; summary?: string; kicker?: string }

interface LangCtx {
  lang: string;
  setLang: (l: string) => void;
  t: (key: string) => string;
  cardMap: Record<string, CardTx>;
  want: (slug: string) => void;
}

const Ctx = createContext<LangCtx>({ lang: "en", setLang: () => {}, t: (k) => STRINGS.en[k] || k, cardMap: {}, want: () => {} });

const detect = () => {
  const saved = localStorage.getItem("nl_lang");
  if (saved && SUPPORTED.includes(saved)) return saved;
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("pt")) return "pt";
  if (nav.startsWith("zh")) return "zh";
  const two = nav.slice(0, 2);
  return SUPPORTED.includes(two) ? two : "en";
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [lang, setLangState] = useState<string>(detect);
  const [cardMap, setCardMap] = useState<Record<string, CardTx>>({});
  const langRef = useRef(lang);
  const pending = useRef<Set<string>>(new Set());
  const requested = useRef<Set<string>>(new Set());
  const timer = useRef<any>(null);

  useEffect(() => {
    langRef.current = lang;
    dayjs.locale(LANGUAGES.find((l) => l.code === lang)?.dayjs || "en");
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (l: string) => {
    if (!SUPPORTED.includes(l)) return;
    localStorage.setItem("nl_lang", l);
    requested.current.clear();
    pending.current.clear();
    setCardMap({});
    setLangState(l);
  };

  const flush = useCallback(async () => {
    timer.current = null;
    const slugs = [...pending.current];
    pending.current.clear();
    const l = langRef.current;
    if (!slugs.length || l === "en") return;
    for (let i = 0; i < slugs.length; i += 30) {
      const chunk = slugs.slice(i, i + 30);
      try {
        const { data } = await api.post("/translate-feed", { slugs: chunk, lang: l });
        if (langRef.current === l) setCardMap((m) => ({ ...m, ...data.items }));
      } catch {
        chunk.forEach((s) => requested.current.delete(s));
      }
    }
  }, []);

  const want = useCallback((slug: string) => {
    if (!slug || langRef.current === "en" || requested.current.has(slug)) return;
    requested.current.add(slug);
    pending.current.add(slug);
    if (!timer.current) timer.current = setTimeout(flush, 400);
  }, [flush]);

  const t = useCallback((key: string) => STRINGS[lang]?.[key] || STRINGS.en[key] || key, [lang]);

  return <Ctx.Provider value={{ lang, setLang, t, cardMap, want }}>{children}</Ctx.Provider>;
};

export const useLang = () => useContext(Ctx);

export const useCardTx = (slug: string): CardTx | undefined => {
  const { lang, cardMap, want } = useContext(Ctx);
  useEffect(() => {
    if (lang !== "en") want(slug);
  }, [slug, lang, want]);
  return lang === "en" ? undefined : cardMap[slug];
};

export const TxTitle = ({ slug, title }: { slug: string; title: string }) => {
  const tx = useCardTx(slug);
  return <>{tx?.title || title}</>;
};

export const articlePath = (lang: string, slug: string) =>
  lang === "en" ? `/article/${slug}` : `/${lang}/article/${slug}`;

export const useArticlePath = () => {
  const { lang } = useContext(Ctx);
  return useCallback((slug: string) => articlePath(lang, slug), [lang]);
};
